/* Photo Motif — looping carousel.
   Vanilla, no dependencies.

   The scrolling itself is native CSS scroll-snap, so the track swipes, snaps
   and works with a keyboard whether this file loads or not. What this adds is
   the pair of arrows and the endless loop.

   The loop is done by cloning the whole set once on each side and quietly
   correcting scrollLeft by one block whenever the track settles outside the
   middle block. Nothing animates during the correction, so the seam is not
   visible: the slide under your eyes is already in the same place.

   Clones are decoration, not content. They are hidden from assistive
   technology, taken out of the tab order, and their comparators are left
   uninitialised — by the time a correction has run you are back on the real
   slide, so a clone is never the thing you interact with. Without JavaScript
   there are no clones at all and the track simply stops at both ends. */

(function () {
  'use strict';

  var SETTLE_MS = 150;

  var motionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function scrollBehaviour() {
    return motionQuery && motionQuery.matches ? 'auto' : 'smooth';
  }

  function setup(track) {
    if (track.dataset.pmCarouselReady) return;
    track.dataset.pmCarouselReady = 'true';

    var root = track.closest('.shopify-section') || track.parentElement;
    var viewport = track.closest('[data-pm-carousel-viewport]') || track.parentElement;
    var arrows = root.querySelector('[data-pm-carousel-arrows]');
    var prev = root.querySelector('[data-pm-carousel-prev]');
    var next = root.querySelector('[data-pm-carousel-next]');
    if (!arrows || !prev || !next) return;

    var originals = Array.prototype.slice.call(track.children);
    if (!originals.length) return;

    arrows.classList.add('is-ready');

    /* ---------------------------------------------------------------- loop */

    var looping = originals.length > 1;

    if (looping) {
      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();

      originals.forEach(function (item) {
        before.appendChild(makeClone(item));
        after.appendChild(makeClone(item));
      });

      track.insertBefore(before, originals[0]);
      track.appendChild(after);
    }

    function makeClone(item) {
      var clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.pmCarouselClone = 'true';
      /* Never announced, never tabbed to, never wired up. */
      clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]').forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
      clone.querySelectorAll('[data-pm-comparator]').forEach(function (el) {
        el.dataset.pmComparatorReady = 'true';
      });
      return clone;
    }

    /* Distance from one block of slides to the next, measured rather than
       computed, so gaps and scroll padding cannot get it wrong. */
    function blockWidth() {
      if (!looping) return 0;
      var first = track.children[0];
      var firstReal = track.children[originals.length];
      return firstReal.offsetLeft - first.offsetLeft;
    }

    function normalise() {
      if (!looping) return;
      var block = blockWidth();
      if (!block) return;
      if (track.scrollLeft >= block * 2 - 1) track.scrollLeft -= block;
      else if (track.scrollLeft < block - 1) track.scrollLeft += block;
    }

    function toStart() {
      if (!looping) return;
      track.scrollLeft = blockWidth();
    }

    /* ------------------------------------------------------------- controls */

    /* One click, one example — at every width. Measured off the first slide so
       it follows the breakpoint without being told about it. */
    function step() {
      var item = track.querySelector('.pm-gallery__item') || track.firstElementChild;
      if (!item) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    prev.addEventListener('click', function () {
      track.scrollBy({ left: -step(), behavior: scrollBehaviour() });
    });
    next.addEventListener('click', function () {
      track.scrollBy({ left: step(), behavior: scrollBehaviour() });
    });

    /* The arrows centre on the photograph, which is shorter than the slide —
       the label sits underneath it. CSS cannot read that height, so it is
       measured and handed over as a custom property. */
    function measure() {
      var photo = track.querySelector('.pm-comparator');
      if (!photo) return;
      var h = photo.getBoundingClientRect().height;
      if (h) viewport.style.setProperty('--pm-gallery-photo-h', h + 'px');
    }

    /* Correct only once the track has come to rest: reassigning scrollLeft
       mid-flight fights the smooth scroll and the momentum on a phone. */
    var settleTimer = null;
    track.addEventListener(
      'scroll',
      function () {
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(normalise, SETTLE_MS);
      },
      { passive: true }
    );

    var lastWidth = track.clientWidth;
    function onResize() {
      measure();
      if (track.clientWidth !== lastWidth) {
        lastWidth = track.clientWidth;
        toStart();
      }
    }

    if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(track);
    else window.addEventListener('resize', onResize);

    measure();
    toStart();
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-pm-carousel-track]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
